/**
 * Générateur — Automatisme : dans le plan muni d'un repère orthogonal, lire
 * les coordonnées d'un point donné ; placer un point de coordonnées données.
 * Attendu ID : 1114 | Niveau : 4ème | Domaine : Repérage
 * Rubrique : AUTOMATISME (programme 2026, Repérage sur une droite et dans le plan 4e).
 * Labels (cartes exerciseur) :
 *   - Lire les coordonnées           (texte à trous en saisie « A( … ; … ) » ; QCM à quatre couples)
 *   - Placer le point                (interactif placer_points_2d : un point, pas 1 ; 0,5 ou 0,25 ;
 *                                     QCM sur figure : quatre points nommés, un seul a les coordonnées demandées)
 *   - Vrai ou faux ?                 (V/F sur figure : abscisse, ordonnée, couple, sur un axe, côté)
 *   - Le repère : vrai ou faux ?     (V/F de pur rappel)
 *
 * ── Plan de cartes (feu vert donné d'avance, commande du 2026-09-10) ────────
 * Source : BO cycle 4 (2026), Repérage 4e + grille P1-P16.
 * A. P1 : CM2 et 6e — aucun repère du plan (P5 du cadrage : « ce qui n'existe
 * pas avant la 5e : le repère du plan »), 5e introduit (se repérer dans le
 * plan, coordonnées relatives), 4e automatisme. P2 : sens installés — la
 * droite graduée (1112-1113), le couple ordonné (abscisse ; ordonnée) ; sens
 * neuf en 5e — deux axes, l'ordre du couple, le signe selon le côté.
 * Distracteurs : couple inversé (ordonnée ; abscisse), signe d'une
 * coordonnée, graduation voisine. P3 : coordonnées entières de −5 à 5, un
 * régime en demi-unités. P4 : sans objet.
 * B. P5 : repère orthogonal carré, axes fléchés, graduations aux entiers,
 * quadrillage (moteur svgRepere2D du 183). P6 : aucun instrument ; placement
 * au clic. P7 : pas de vecteur, pas de distance. P8 : repère, origine, axe
 * des abscisses, axe des ordonnées, abscisse, ordonnée, coordonnées.
 * C. P9 : lecture d'une droite graduée avec relatifs (1113). P10 : constat.
 * P11 : l'ordre du couple (2 ; 3) ≠ (3 ; 2) ; un point sur un axe a une
 * coordonnée nulle. P12 : amont en 5e seulement, rémanence courte : rappel V/F.
 * D. P13 : erreurs → leurres : couple inversé, signe de x, signe de y,
 * voisin d'une unité. P14 : aucun calcul, lecture de deux graduations. P15 :
 * coordonnées SUR le quadrillage, un seul point nommé porte le couple
 * demandé ; jamais deux points symétriques dans un QCM sans que le couple
 * demandé les distingue. P16 : trous en saisie avec l'ordre fixé par la
 * notation (abscisse puis ordonnée, ce n'est PAS une commutativité : l'ordre
 * est le savoir) ; virgule française ; placer_points_2d : « x;y » canonique.
 * INVARIANTS : arithmétique en DIXIÈMES entiers ; figure avec data-points
 * pour les solveurs ; V/F sans crochets, mixité 1-3 faux, familles exclusives.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, basePayload,
    retirerCodesErreur,
} from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';
import { svgRepere2D } from '../espace/svgEspace';

const ATTENDU_ID = 1114;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_LIRE = 'Lire les coordonnées';
const L_PLACER = 'Placer le point';
const L_VF = 'Vrai ou faux ?';
const L_RAPPEL = 'Le repère : vrai ou faux ?';

const B = 50;                                                      // repère de −5 à 5 (dixièmes)
const fr = (d) => { const s = d < 0 ? '-' : ''; const a = Math.abs(d); return s + (a % 10 === 0 ? String(a / 10) : `${Math.floor(a / 10)},${a % 10}`); };
const couple = (p) => `(${fr(p.x)} ; ${fr(p.y)})`;
const LETTRES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

/** Régime : entiers (pas 1) trois fois sur quatre, demi-unités sinon. */
const tirerRegime = () => (Math.random() < 0.75 ? { nom: 'entiers', pas: 10 } : { nom: 'demis', pas: 5 });
/** Un point du quadrillage, jamais l'origine, jamais sur les bords ; `surAxe` force une coordonnée nulle. */
const tirerPoint = (reg, surAxe = false) => {
    for (let essai = 0; essai < 100; essai++) {
        let x = reg.pas === 10 ? randInt(-4, 4) * 10 : randInt(-8, 8) * 5; let y = reg.pas === 10 ? randInt(-4, 4) * 10 : randInt(-8, 8) * 5;
        if (surAxe) { if (Math.random() < 0.5) x = 0; else y = 0; }
        if (x === 0 && y === 0) continue;
        return { x, y };
    }
    return { x: 20, y: -30 };
};
const egal = (p, q) => p.x === q.x && p.y === q.y;
const dansRepere = (p) => Math.abs(p.x) <= B - 10 && Math.abs(p.y) <= B - 10;
/** n points distincts, dont au moins un à coordonnées de signes mixtes ou négatives. */
const tirerPoints = (reg, n, avecAxe = false) => {
    const pts = [];
    // au moins 1 unité d'écart entre deux points (revue 2026-09-10 : en demi-unités, des points à 0,5 l'un de l'autre)
    while (pts.length < n) { const p = tirerPoint(reg, avecAxe && pts.length === n - 1); if (!pts.some((q) => Math.max(Math.abs(q.x - p.x), Math.abs(q.y - p.y)) < 10)) pts.push(p); }
    if (!pts.some((p) => p.x < 0 || p.y < 0)) pts[0] = { x: -Math.abs(pts[0].x) || -10, y: pts[0].y };
    const lettres = shuffle(LETTRES).slice(0, n);
    return pts.map((p, i) => ({ ...p, label: lettres[i] }));
};
/** Leurres de lecture d'un couple : inversé, signe de x, signe de y, voisin. */
const leurresDe = (p, reg) => {
    const cands = [
        { x: p.y, y: p.x, role: 'inverse' },
        { x: -p.x, y: p.y, role: 'signe_x' },
        { x: p.x, y: -p.y, role: 'signe_y' },
        { x: p.x + randomChoice([-1, 1]) * reg.pas, y: p.y, role: 'voisin_x' },
        { x: p.x, y: p.y + randomChoice([-1, 1]) * reg.pas, role: 'voisin_y' },
    ];
    const pris = [];
    cands.forEach((c) => { if (!egal(c, p) && dansRepere(c) && !(c.x === 0 && c.y === 0) && !pris.some((q) => egal(q, c))) pris.push(c); });
    return pris;
};

const figure = (reg, points) => {
    const svg = svgRepere2D({ bD: B, pasD: reg.pas, points: points.map((p) => ({ xD: p.x, yD: p.y, label: p.label })) });
    const data = points.map((p) => `${p.label}:${fr(p.x)};${fr(p.y)}`).join('|');
    return svg.replace('<svg ', `<svg data-points="${data}" data-pas="${fr(reg.pas)}" `);
};

// ─── Codes d'erreur (contenu.erreurs) ──────────────────────────────────────
// Lot 2 (04/10/2026, docs/SFD_CODES_ERREUR.md) : une coordonnée lue avec le mauvais signe a son propre code, signe_de_coordonnee_a_l_envers.
/** Les couples qu'une erreur connue donne pour le point p, au pas de la figure : [{ code, x, y }]. */
const erreursCouple = (p, pas) => {
    const e = [{ code: 'abscisse_ordonnee_echangees', x: p.y, y: p.x }, { code: 'signe_de_coordonnee_a_l_envers', x: -p.x, y: p.y }, { code: 'signe_de_coordonnee_a_l_envers', x: p.x, y: -p.y }];
    [-pas, pas].forEach((k) => e.push({ code: 'traits_au_lieu_d_intervalles', x: p.x + k, y: p.y }, { code: 'traits_au_lieu_d_intervalles', x: p.x, y: p.y + k }));
    return e.filter((q) => !egal(q, p));
};
/** Le code d'un couple, s'il est seul à y mener (null sinon). */
const codeDuCouple = (erreurs, q) => { const c = [...new Set(erreurs.filter((e) => egal(e, q)).map((e) => e.code))]; return c.length === 1 ? c[0] : null; };
/** Les valeurs qu'une erreur connue donne pour une coordonnée v (l'autre coordonnée du point étant w). */
const erreursCoordonnee = (v, w, pas) => [
    { code: 'abscisse_ordonnee_echangees', v: w }, { code: 'signe_de_coordonnee_a_l_envers', v: -v },
    { code: 'traits_au_lieu_d_intervalles', v: v - pas }, { code: 'traits_au_lieu_d_intervalles', v: v + pas },
].filter((e) => e.v !== v);
/** Le code d'une valeur, s'il est seul à y mener (null sinon). */
const codeDeValeur = (erreurs, u) => { const c = [...new Set(erreurs.filter((e) => e.v === u).map((e) => e.code))]; return c.length === 1 ? c[0] : null; };

// ─── Anti-répétition ───────────────────────────────────────────────────────
const antiRepeat = (construire, ref) => {
    let payload = construire();
    for (let t = 0; t < 8 && payload._meta.params.signature === ref.v; t++) payload = construire();
    ref.v = payload._meta.params.signature;
    return payload;
};
const refL = { v: null }; const refQ = { v: null }; const refP = { v: null }; const refB = { v: null };

// ─── Carte 1 : lire les coordonnées ────────────────────────────────────────

const construireLireTrou = () => {
    const reg = tirerRegime(); const P = tirerPoints(reg, 3, Math.random() < 0.5);          // un point sur un axe une fois sur deux (revue 2026-09-10 : c'était systématique)
    const phrase = P.map((p) => `${p.label} ( {{${fr(p.x)}}} ; {{${fr(p.y)}}} )`).join(' ; ') + '.';
    const payload = makeTexteTrou(phrase, '<p>Lis les coordonnées de chaque point. Écris d\'abord l\'abscisse, puis l\'ordonnée, avec les signes.</p>', { ...opts(L_LIRE), mode: 'saisie', elements_additionnels: imgElement(figure(reg, P), 'medium') });
    payload.contenu = { ...payload.contenu, sousType: reg.nom };
    payload._meta.params = { regime: reg.nom, points: P, signature: `${reg.nom}-${P.map((p) => `${p.x},${p.y}`).sort().join('|')}` };
    // trou 2k : l'abscisse du point k, trou 2k + 1 : son ordonnée ; rien pour la même coordonnée d'un autre point tracé
    const erreurs = [];
    P.forEach((p, k) => [['x', 'y'], ['y', 'x']].forEach(([cle, autre], j) => {
        const E = erreursCoordonnee(p[cle], p[autre], reg.pas);
        [...new Set(E.map((e) => e.v))].forEach((u) => {
            const code = codeDeValeur(E, u);
            if (code && !P.some((q) => q !== p && q[cle] === u)) erreurs.push({ code, trou: 2 * k + j, valeur: fr(u) });
        });
    }));
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genLireTrou = () => antiRepeat(construireLireTrou, refL);

const construireLireQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const reg = tirerRegime(); const P = tirerPoints(reg, 3);
        const cible = randomChoice(P.filter((p) => p.x < 0 || p.y < 0));
        const leurres = shuffle(leurresDe(cible, reg)).slice(0, 3); if (leurres.length < 3) continue;
        // options écrites avec le nom du point : « H(−3,5 ; 4) » (demande prof 2026-09-11)
        const nomme = (p) => `${cible.label}${couple(p)}`;
        const options = shuffle([nomme(cible), ...leurres.map(nomme)]);
        if (new Set(options).size !== 4) continue;
        const payload = makeQcm(options, nomme(cible), `<p>Quelles sont les coordonnées du point ${cible.label} ?</p>`, { ...opts(L_LIRE), elements_additionnels: imgElement(figure(reg, P), 'medium') });
        payload.contenu = { ...payload.contenu, sousType: reg.nom };
        payload._meta.params = { regime: reg.nom, cible: { x: cible.x, y: cible.y }, roles: leurres.map((l) => l.role), points: P, signature: `${reg.nom}-${cible.x},${cible.y}` };
        // chaque leurre nommé par l'erreur qui le donne, sauf s'il est le couple d'un autre point tracé
        const E = erreursCouple(cible, reg.pas);
        const erreurs = leurres.filter((l) => !P.some((q) => egal(q, l))).map((l) => ({ code: codeDuCouple(E, l), valeur: nomme(l) })).filter((x) => x.code);
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1114 : QCM sans leurres');
};
const genLireQcm = () => antiRepeat(construireLireQcm, refQ);

// ─── Carte 2 : placer le point (interactif) ────────────────────────────────

// Placement interactif : trois pas (1 ; 0,5 ; 0,25), en CENTIÈMES ici (0,25 n'est pas un nombre de dixièmes).
// Le composant Repere2DInteractiveEleve étiquette les entiers, les demis en petit, jamais les quarts (prof, 2026-09-10).
const frC = (c) => { const s = c < 0 ? '-' : ''; const a = Math.abs(c); const e = Math.floor(a / 100); const r = a % 100; return s + (r === 0 ? String(e) : `${e},${String(r).padStart(2, '0').replace(/0$/, '')}`); };
const construirePlacer = () => {
    const reg = randomChoice([{ nom: 'entiers', pas: 100 }, { nom: 'entiers', pas: 100 }, { nom: 'demis', pas: 50 }, { nom: 'quarts', pas: 25 }]);
    let x = 0; let y = 0;
    for (let essai = 0; essai < 100 && (x === 0 && y === 0); essai++) {
        x = randInt(-4 * (100 / reg.pas), 4 * (100 / reg.pas)) * reg.pas; y = randInt(-4 * (100 / reg.pas), 4 * (100 / reg.pas)) * reg.pas;
        if (Math.random() < 0.2) { if (Math.random() < 0.5) x = 0; else y = 0; }                         // un point sur un axe, une fois sur cinq
        if (reg.pas < 100 && x % 100 === 0 && y % 100 === 0) { x = 0; y = 0; }                             // en demis / quarts : au moins une coordonnée non entière
    }
    if (x === 0 && y === 0) { x = 200; y = -300; }
    const label = randomChoice(LETTRES);
    const payload = basePayload('association',
        `<p>Place le point ${label} de coordonnées (${frC(x)} ; ${frC(y)}) dans le repère.</p>`,
        {
            subType: 'placer_points_2d',
            repere: { xMin: -5, yMin: -5, xMax: 5, yMax: 5, xStep: reg.pas / 100, yStep: reg.pas / 100 },
            pointsToPlace: [label],
            dejaPlaces: [],
            bonne_reponse: JSON.stringify({ [label]: `${frC(x)};${frC(y)}` }),
            sousType: reg.nom,
            // Le point fantôme n'affiche pas ses coordonnées : placer un point EST le savoir évalué,
            // le texte « (x ; y) » sous le curseur donnait la réponse (demande prof 2026-09-18, §9bis).
            masquerCoordonnees: true,
        },
        opts(L_PLACER));
    payload._meta.params = { regime: reg.nom, point: { x: x / 100, y: y / 100 }, signature: `${reg.nom}-${x},${y}` };
    // positions fausses nommées par les calculs des leurres du QCM (en centièmes), dans le repère
    const E = erreursCouple({ x, y }, reg.pas);
    const erreurs = [];
    E.forEach((q) => {
        const valeur = `${frC(q.x)};${frC(q.y)}`; const code = codeDuCouple(E, q);
        if (code && Math.abs(q.x) <= 500 && Math.abs(q.y) <= 500 && !erreurs.some((e) => e.valeur === valeur)) erreurs.push({ code, point: label, valeur });
    });
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genPlacer = () => antiRepeat(construirePlacer, refP);

// ─── Carte 2b : le bon point (QCM, rattaché à « Placer le point », comme le 1112) ─

const construireBon = () => {
    for (let essai = 0; essai < 60; essai++) {
        const reg = tirerRegime(); const cible = tirerPoint(reg);
        if (!(cible.x < 0 || cible.y < 0) || Math.abs(cible.x) === Math.abs(cible.y)) continue;      // |x| ≠ |y| : l'inversé et les signes sont tous distincts
        // lisibilité des noms (prof, 2026-09-10) : cible à 2 unités au moins des axes, voisins à 1 unité, 1 unité au moins entre deux candidats
        if (Math.abs(cible.x) < 20 || Math.abs(cible.y) < 20) continue;
        const leurres = shuffle(leurresDe(cible, { ...reg, pas: 10 })).slice(0, 3); if (leurres.length < 3) continue;
        const cands = [cible, ...leurres]; const ecart = (p, q) => Math.max(Math.abs(p.x - q.x), Math.abs(p.y - q.y));
        if (cands.some((p, i) => cands.some((q, j) => j > i && ecart(p, q) < 10))) continue;
        const lettres = shuffle(['A', 'B', 'C', 'D']);
        const points = [{ ...cible, role: 'cible' }, ...leurres].map((c, i) => ({ ...c, label: lettres[i] }));
        const bonne = points.find((c) => c.role === 'cible').label;
        const payload = makeQcm(['A', 'B', 'C', 'D'], bonne, `<p>Quel point a pour coordonnées ${couple(cible)} ?</p>`, { ...opts(L_PLACER), elements_additionnels: imgElement(figure(reg, points), 'medium') });
        payload.contenu.choix = ['A', 'B', 'C', 'D']; payload.contenu.bonne_reponse = bonne;
        payload.contenu = { ...payload.contenu, sousType: reg.nom };
        payload._meta.params = { regime: reg.nom, cible, points: points.map((c) => ({ label: c.label, role: c.role, x: c.x, y: c.y })), signature: `${reg.nom}-${cible.x},${cible.y}` };
        // chaque point leurre nommé ; la graduation voisine seulement au pas 1 (au pas 0,5, le leurre est à deux graduations)
        const E = erreursCouple(cible, reg.pas);
        const erreurs = points.filter((c) => c.role !== 'cible').map((c) => ({ code: codeDuCouple(E, c), valeur: c.label })).filter((x) => x.code);
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1114 : bon point sans candidats');
};
const genBon = () => antiRepeat(construireBon, refB);

// ─── Carte 4 : vrai ou faux ? (sur figure) ─────────────────────────────────

let derniereEmpreinteVf1114 = '';

const genVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const reg = tirerRegime(); const P = tirerPoints(reg, 4, Math.random() < 0.5);
        const [a, b, c, e] = P;
        const familles = [
            { f: 'abscisse', vrai: `L'abscisse du point ${a.label} est ${fr(a.x)}.`, faux: `L'abscisse du point ${a.label} est ${fr(a.y === a.x ? -a.x : a.y)}.` },
            { f: 'ordonnee', vrai: `L'ordonnée du point ${b.label} est ${fr(b.y)}.`, faux: `L'ordonnée du point ${b.label} est ${fr(b.x === b.y ? -b.y : b.x)}.` },
            { f: 'couple', vrai: `Le point ${c.label} a pour coordonnées ${couple(c)}.`, faux: `Le point ${c.label} a pour coordonnées ${couple({ x: c.y, y: c.x })}.` },
            { f: 'axe', vrai: e.y === 0 ? `Le point ${e.label} est sur l'axe des abscisses.` : e.x === 0 ? `Le point ${e.label} est sur l'axe des ordonnées.` : null, faux: e.y === 0 ? `Le point ${e.label} est sur l'axe des ordonnées.` : e.x === 0 ? `Le point ${e.label} est sur l'axe des abscisses.` : `Le point ${e.label} est sur l'axe des ${randomChoice(['abscisses', 'ordonnées'])}.` },
            { f: 'cote', vrai: `Le point ${b.label} est ${b.y > 0 ? 'au-dessus de l\'axe' : b.y < 0 ? 'en dessous de l\'axe' : 'sur l\'axe'} des abscisses.`, faux: b.y === 0 ? null : `Le point ${b.label} est ${b.y > 0 ? 'en dessous' : 'au-dessus'} de l'axe des abscisses.` },
        ];
        // le couple inversé ne trompe pas si x = y
        // (et la famille « côté » n'a pas de sens quand le point est sur l'axe : ce serait la famille « axe »)
        const dispo = familles.filter((fa) => !(fa.f === 'couple' && c.x === c.y) && !(fa.f === 'cote' && b.y === 0));
        const retenues = shuffle(dispo).slice(0, 4); if (retenues.length < 4) continue;
        const vraiesPossibles = retenues.filter((fa) => fa.vrai).length; const faussesPossibles = retenues.filter((fa) => fa.faux).length;
        const minFaux = Math.max(1, 4 - vraiesPossibles); const maxFaux = Math.min(3, faussesPossibles); if (minFaux > maxFaux) continue;
        const nbFaux = randInt(minFaux, maxFaux);
        const ordre = shuffle(retenues); const lignes = []; let restants = nbFaux;
        ordre.forEach((fa, i) => {
            const peutFaux = !!fa.faux; const peutVrai = !!fa.vrai; const restantsLignes = ordre.length - i;
            const auFaux = peutFaux && restants > 0 && (!peutVrai || restants >= restantsLignes || Math.random() < 0.5);
            if (auFaux) { lignes.push({ l: fa.faux, v: false, f: fa.f }); restants--; } else lignes.push({ l: fa.vrai, v: true, f: fa.f });
        });
        if (lignes.some((it) => !it.l) || lignes.filter((it) => !it.v).length !== nbFaux) continue;
        const empreinte = lignes.map((it) => it.l).sort().join('#');
        if (empreinte === derniereEmpreinteVf1114) continue;
        derniereEmpreinteVf1114 = empreinte;
        const melange = shuffle(lignes);
        const payload = makeVraiFaux(
            melange.map((it) => it.l),
            melange.map((it) => (it.v ? 0 : 1)),
            '<p>Observe le repère. Pour chaque affirmation, dis si elle est vraie ou fausse.</p>',
            { ...opts(L_VF), elements_additionnels: imgElement(figure(reg, P), 'medium') },
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { regime: reg.nom, points: P, nbFaux };
        // l'idée fausse de chaque ligne fausse (x = y : −x est une erreur de signe ; axeDit : la coordonnée nulle sur l'axe nommé) ; rien si un autre point la rend vraie
        const ailleurs = (pt, cle, u) => P.some((q) => q !== pt && q[cle] === u);
        const vA = a.y === a.x ? -a.x : a.y; const vB = b.x === b.y ? -b.y : b.x;
        const axeDit = e.y === 0 ? 'x' : e.x === 0 ? 'y' : null;
        const codeFaux = {
            abscisse: ailleurs(a, 'x', vA) ? null : codeDeValeur(erreursCoordonnee(a.x, a.y, reg.pas), vA),
            ordonnee: ailleurs(b, 'y', vB) ? null : codeDeValeur(erreursCoordonnee(b.y, b.x, reg.pas), vB),
            couple: P.some((q) => q !== c && q.x === c.y && q.y === c.x) ? null : codeDuCouple(erreursCouple(c, reg.pas), { x: c.y, y: c.x }),
            axe: axeDit && !ailleurs(e, axeDit, 0) ? 'abscisse_ordonnee_echangees' : null,
        };
        const erreurs = melange.filter((it) => !it.v && codeFaux[it.f]).map((it) => ({ code: codeFaux[it.f], ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1114 : V/F sans tirage après 160 essais');
};

// ─── Carte 5 : le repère, vrai ou faux ? (pur rappel) ──────────────────────

const POOL_RAPPEL = [
    { l: 'Les coordonnées d\'un point s\'écrivent dans l\'ordre (abscisse ; ordonnée).', v: true, f: 'ordre' },
    { l: 'Les coordonnées d\'un point s\'écrivent dans l\'ordre (ordonnée ; abscisse).', v: false, f: 'ordre', code: 'abscisse_ordonnee_echangees' },
    { l: 'L\'abscisse se lit sur l\'axe horizontal, l\'ordonnée sur l\'axe vertical.', v: true, f: 'axes' },
    { l: 'L\'abscisse se lit sur l\'axe vertical.', v: false, f: 'axes', code: 'abscisse_ordonnee_echangees' },
    { l: 'L\'origine du repère a pour coordonnées (0 ; 0).', v: true, f: 'origine' },
    { l: 'L\'origine du repère a pour coordonnées (1 ; 1).', v: false, f: 'origine' },
    { l: 'Un point situé sur l\'axe des ordonnées a une abscisse égale à 0.', v: true, f: 'sur_axe' },
    { l: 'Un point situé sur l\'axe des ordonnées a une ordonnée égale à 0.', v: false, f: 'sur_axe', code: 'abscisse_ordonnee_echangees' },
    { l: 'Un point d\'ordonnée négative est en dessous de l\'axe des abscisses.', v: true, f: 'signe' },
    { l: 'Un point d\'abscisse négative est au-dessus de l\'axe des abscisses.', v: false, f: 'signe' },
    { l: 'Les couples (2 ; 3) et (3 ; 2) désignent deux points différents.', v: true, f: 'couple' },
    // Relecture du 04/10/2026 (juge du 03/10) : croire que l'ordre ne compte pas n'est pas échanger les axes, sans nom.
    { l: 'Les couples (2 ; 3) et (3 ; 2) désignent le même point.', v: false, f: 'couple' },
    { l: 'Pour placer le point de coordonnées (−2 ; 3), on part de l\'origine, on va de 2 vers la gauche puis de 3 vers le haut.', v: true, f: 'geste' },
    { l: 'Pour placer le point de coordonnées (−2 ; 3), on part de l\'origine, on va de 2 vers le haut puis de 3 vers la gauche.', v: false, f: 'geste', code: 'abscisse_ordonnee_echangees' },
];

let dernieresLignesRappel1114 = [];

const genRappelVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const fausses = shuffle(POOL_RAPPEL.filter((it) => !it.v)); const vraies = shuffle(POOL_RAPPEL.filter((it) => it.v));
        const nbFaux = randInt(1, 3);
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (new Set(lignes.map((it) => it.f)).size !== 4) continue;
        if (essai > 120) dernieresLignesRappel1114 = [];
        if (lignes.some((it) => dernieresLignesRappel1114.includes(it.l))) continue;
        dernieresLignesRappel1114 = lignes.map((it) => it.l);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation sur le repère du plan, dis si elle est vraie ou fausse.</p>',
            opts(L_RAPPEL),
        );
        payload._meta.params = { lignes: lignes.map((it) => POOL_RAPPEL.indexOf(it)) };
        // le code de chaque affirmation fausse du vivier (aucun pour l'origine (1 ; 1), l'abscisse négative au-dessus ni le même point)
        const erreurs = lignes.filter((it) => !it.v && it.code).map((it) => ({ code: it.code, ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1114 : V/F rappel sans tirage après 160 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    lire_texte_trou: genLireTrou,
    lire_qcm_unique: genLireQcm,
    placer_placer_points: genPlacer,
    placer_qcm_unique: genBon,
    vf_tableau_vf: genVf,
    rappel_tableau_vf: genRappelVf,
};

const FORMAT_LABELS = { texte_trou: 'Texte à trous', qcm_unique: 'QCM', placer_points: 'Placer le point', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'lire', label: L_LIRE, formatsList: buildFormats('lire') },
    { id: 'placer', label: L_PLACER, formatsList: buildFormats('placer') },
    { id: 'vf', label: L_VF, formatsList: buildFormats('vf') },
    { id: 'rappel', label: L_RAPPEL, formatsList: buildFormats('rappel') },
];

// Codes retirés le 03/10/2026 après la lecture à l'aveugle (docs/kit/REGLES_EQUIPE.md) : le nom ne
// décrit pas le choix construit, ou deux erreurs donnent le même nombre, ou le choix est une figure
// que la lecture n'a pas pu vérifier. Le choix reste proposé, il part sans nom.
const CODES_RETIRES = {
    placer_qcm_unique: ['traits_au_lieu_d_intervalles', 'signe_de_coordonnee_a_l_envers', 'abscisse_ordonnee_echangees'],
    vf_tableau_vf: ['abscisse_ordonnee_echangees', 'signe_de_coordonnee_a_l_envers'],
};

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    retirerCodesErreur(payload, CODES_RETIRES[key]);
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { tirerPoint, leurresDe, fr, couple, POOL_RAPPEL };
